import { BTServiceFileUpload } from "legacyComponents/FileUploadContainer.types";
import moment from "moment";

import { type CurrencyLocale } from "helpers/AppProvider.types";

import { CustomFieldAssociatedType, CustomFieldDataType } from "types/enum";

import {
    type CustomField,
    type CustomFieldMetadata,
    type ServerGridColumn,
} from "utilities/list/list.types";
import { formatCurrency } from "utilities/number/number";
import { isNullOrUndefined } from "utilities/object/object";
import { isNullOrWhitespace } from "utilities/string/string";

import { type ICustomFieldFormValues } from "commonComponents/entity/customField/CustomFieldContainer/CustomFieldContainer";
import {
    type ICustomFieldOptionsList,
    type ICustomFieldResponse,
} from "commonComponents/entity/customField/CustomFieldContainer/CustomFieldContainer.types";
import { customFieldDataTypeName } from "commonComponents/entity/customField/utils/customFieldDataTypeName";
import { type CustomFieldDefinition } from "commonComponents/entity/customField/utils/customFieldDefinition.util";
import { parseCommaSeparatedIds } from "commonComponents/entity/customField/utils/customFieldTypes/customFieldSharedUtils";
import {
    getOptionText,
    getOptionTexts,
} from "commonComponents/entity/customField/utils/customFieldTypes/selectCustomFieldUtils";
import { type IDateDisplayFormattingProps } from "commonComponents/utilities/DateDisplay/DateDisplayDate.utilities";

export const createCustomFieldLookup = (
    columns: ServerGridColumn[] | null,
): { [customFieldId: string]: CustomFieldMetadata } => {
    if (columns === null) {
        return {};
    }
    return columns
        .filter((c) => c.customFieldMetadata !== null && c.customFieldMetadata !== undefined)
        .reduce(
            (obj, column) => ({
                ...obj,
                [column.jsonKey]: column.customFieldMetadata,
            }),
            {},
        );
};

export const customFieldHasValue = (customField: ICustomFieldResponse | CustomField) => {
    const value = customField.value;
    const resolvedType =
        (customField as ICustomFieldResponse)?.typeId ?? (customField as CustomField)?.type;

    const isArrayValue = [
        CustomFieldDataType.ListOfSubs,
        CustomFieldDataType.ListOfUsers,
        CustomFieldDataType.DropDownList,
        CustomFieldDataType.MultiSelectSubs,
        CustomFieldDataType.MultiSelectUsers,
        CustomFieldDataType.DropDownCheckList,
    ].includes(resolvedType);

    const isTextValue = [
        CustomFieldDataType.Text,
        CustomFieldDataType.MultiLineText,
        CustomFieldDataType.Link,
    ].includes(resolvedType);

    const isNumericWrapper = [
        CustomFieldDataType.Currency,
        CustomFieldDataType.WholeNumber,
    ].includes(resolvedType);

    if (isArrayValue) {
        return (
            !isNullOrUndefined(value) &&
            (value as unknown[]).length !== 0 &&
            !(value as unknown[]).includes(-1)
        );
    } else if (isTextValue) {
        return !isNullOrWhitespace(value as string);
    } else if (isNumericWrapper) {
        // Handle legacy wrapped numeric shape { value: number; scale?: number }
        interface INumericWrapper {
            value: unknown;
            scale?: unknown;
        }
        const isNumericWrapperObject = (v: unknown): v is INumericWrapper =>
            typeof v === "object" && v !== null && "value" in (v as Record<string, unknown>);
        if (isNumericWrapperObject(value)) {
            return value.value !== null && value.value !== undefined;
        }
        return !isNullOrUndefined(value);
    }
    return !isNullOrUndefined(value);
};

export function ConvertToTypeName(cfType: CustomFieldAssociatedType): string {
    switch (cfType) {
        case CustomFieldAssociatedType.Lead:
            return "Lead Opportunities";

        case CustomFieldAssociatedType.JobsJobInfo:
            return "Jobs";

        case CustomFieldAssociatedType.Contacts:
            return "Client contacts";

        case CustomFieldAssociatedType.Warranty:
            return "Warranties";

        case CustomFieldAssociatedType.ToDos:
            return "To-Do's";

        case CustomFieldAssociatedType.PurchaseOrderPayments:
            return "Bills";

        case CustomFieldAssociatedType.ChangeOrders:
            return "Change Orders";

        case CustomFieldAssociatedType.OwnerPayments:
            return "Invoices";

        case CustomFieldAssociatedType.DailyLogs:
            return "Daily Logs";

        case CustomFieldAssociatedType.RFIs:
            return "RFIs";

        case CustomFieldAssociatedType.Selections:
            return "Selections";

        case CustomFieldAssociatedType.PurchaseOrders:
            return "Purchase Orders";

        case CustomFieldAssociatedType.Subs:
            return "Subs/vendors";

        case CustomFieldAssociatedType.InternalUsers:
            return "Internal users";

        case CustomFieldAssociatedType.Tasks:
            return "Tasks";

        default:
            throw new Error(
                `Missing switch case in CustomFieldTempFileTypes for CustomFieldAssociatedType: ${cfType}`,
            );
    }
}

/**
 * Intended for use with {@link formatCustomFieldToString} to generate string displays for custom fields.
 * @returns A CustomFieldDefinition object
 */
export const mapResponseToCustomFieldDefinition = (
    response: ICustomFieldResponse,
    optionsList?: ICustomFieldOptionsList,
): CustomFieldDefinition => {
    const base: CustomFieldDefinition = {
        dataType: customFieldDataTypeName(response.typeId) as CustomFieldDefinition["dataType"],
        builderId: 0,
        customFieldId: response.id,
        label: response.title,
        tooltipText: response.toolTipText,
        isRequired: false,
        onOwnerPortal: response.onOwnerPortal,
        onSubPortal: false,
    };

    const needsOptions = [
        CustomFieldDataType.DropDownList,
        CustomFieldDataType.DropDownCheckList,
        CustomFieldDataType.ListOfUsers,
        CustomFieldDataType.ListOfSubs,
        CustomFieldDataType.MultiSelectUsers,
        CustomFieldDataType.MultiSelectSubs,
    ].includes(response.typeId);

    if (needsOptions && optionsList) {
        const rawOptions = optionsList[response.optionsKey] ?? [];
        const definitionWithOptions = {
            ...base,
            options: rawOptions.map((opt, index) => ({
                id: opt.id,
                text: opt.name,
                sortOrder: index,
            })),
        };
        return definitionWithOptions;
    }

    return base;
};

/**
 * Takes in a custom field value, its definitions, and some of the builder's locale information to format custom field values to strings.
 * @returns String representation of the custom field value.
 */
export const formatCustomFieldToString = (
    value: unknown,
    dataType: CustomFieldDataType,
    definition: CustomFieldDefinition | undefined,
    currencyLocale: CurrencyLocale,
    formatDate: (value: Date, options: IDateDisplayFormattingProps) => string,
): string => {
    if (value == null) {
        return "";
    }

    const dateFormatOptions = {
        showDate: true,
        showTime: false,
        showDayOfWeek: false,
        hideCurrentYear: false,
    };

    switch (dataType) {
        case CustomFieldDataType.Text:
        case CustomFieldDataType.MultiLineText:
        case CustomFieldDataType.Link:
            return typeof value === "string" ? value : "";

        case CustomFieldDataType.WholeNumber:
            return typeof value === "number" ? String(value) : "";

        case CustomFieldDataType.Currency:
            return typeof value === "number"
                ? formatCurrency(value, currencyLocale, {
                      decimalScale: 2,
                      fixedDecimalScale: true,
                  })
                : "";

        case CustomFieldDataType.CheckBox:
            return value ? "Yes" : "No";

        case CustomFieldDataType.Date:
            if (value instanceof Date && !isNaN(value.getTime())) {
                return formatDate(value, dateFormatOptions);
            }
            if (typeof value === "string" && value) {
                const date = new Date(value);
                if (!isNaN(date.getTime())) {
                    return formatDate(date, dateFormatOptions);
                }
            }
            return "";

        case CustomFieldDataType.DropDownList: {
            if (typeof value !== "number" || value === -1) return "";
            return getOptionText(value, definition);
        }

        case CustomFieldDataType.DropDownCheckList: {
            if (typeof value !== "string" || !value) return "";
            const ids = parseCommaSeparatedIds(value);
            return getOptionTexts(ids, definition);
        }

        case CustomFieldDataType.ListOfUsers:
        case CustomFieldDataType.ListOfSubs: {
            if (typeof value !== "string" || !value) return "";
            const id = parseInt(value, 10);
            if (isNaN(id) || id <= 0) return "";
            return getOptionText(id, definition);
        }

        case CustomFieldDataType.MultiSelectUsers:
        case CustomFieldDataType.MultiSelectSubs: {
            if (typeof value !== "string" || !value) return "";
            const ids = parseCommaSeparatedIds(value);
            return getOptionTexts(ids, definition);
        }

        case CustomFieldDataType.File:
            return typeof value === "string" ? value : "";

        default:
            return typeof value === "string" ? value : "";
    }
};

/**
 * Intended for use with {@link formatCustomFieldToString} to generate string displays for custom fields.
 * Adapts a legacy custom field form value to a simpler representation.
 * @returns A simplified representation of the object.
 */
export const normalizeCustomFieldFormValueForDisplay = (
    value: ICustomFieldFormValues["value"],
    dataType: CustomFieldDataType,
): unknown => {
    if (value === null) {
        return null;
    }

    if (moment.isMoment(value)) {
        return value.toDate();
    }

    switch (dataType) {
        case CustomFieldDataType.WholeNumber:
        case CustomFieldDataType.Currency:
            return typeof value === "string" ? Number(value) : value;

        case CustomFieldDataType.DropDownList:
            return Array.isArray(value) ? (value[0] ?? -1) : value;

        case CustomFieldDataType.ListOfUsers:
        case CustomFieldDataType.ListOfSubs:
            // eslint-disable-next-line @typescript-eslint/no-base-to-string
            return Array.isArray(value) ? String(value[0] ?? "") : String(value);

        case CustomFieldDataType.DropDownCheckList:
        case CustomFieldDataType.MultiSelectUsers:
        case CustomFieldDataType.MultiSelectSubs:
            return Array.isArray(value) ? value.join(",") : value;

        case CustomFieldDataType.File:
            return value instanceof BTServiceFileUpload ? value.fileName : value;

        default:
            return value;
    }
};
